import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AcceptInviteFormFields from "./AcceptInviteFormFields";
import { FormData, FormErrors, RegisterResponse } from "../../types/types";
import { usePostAuth } from "../../hooks/usePostAuth";

const AcceptInviteForm: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    gender: "",
    dob: "",
    company: "",
    password: "",
    password_confirmation: "",
    token: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const registerMutation = usePostAuth("/register", {
    onSuccess: (data: RegisterResponse) => {
      setSuccessMessage("Welcome aboard!");

      localStorage.setItem("_token", data.token);
      const role = data.user.role;
      localStorage.setItem("role", role);

      const name = data?.user?.name;
      const photo = data?.user?.photo_url;
      const email = data?.user?.email;

      localStorage.setItem("details", JSON.stringify({ name, photo, email }));

      navigate("/dashboard");
    },
    onError: (error: unknown) => {
      setErrors({ server: (error as Error).message });
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const email = params.get("email");
    const token = params.get("token");

    if (!email || !token) {
      navigate("/", { replace: true });
      return;
    }

    setFormData((prev) => ({ ...prev, email, token }));
  }, [location.search, navigate]);

  const validateDOB = (dob: string): string | undefined => {
    if (!dob) return "Date of birth is required";

    const today = new Date();
    const birthDate = new Date(dob);
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const dayDiff = today.getDate() - birthDate.getDate();

    const adjustedAge =
      monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

    if (adjustedAge < 18) return "You must be at least 18 years old";
    if (birthDate > today) return "Date of birth cannot be in the future";
    return undefined;
  };

  const validate = (): FormErrors => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) newErrors.name = "Full name is required";

    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    else if (!/^[0-9]{11}$/.test(formData.phone.trim()))
      newErrors.phone = "Please enter a valid 11-digit phone number";

    if (!formData.gender) newErrors.gender = "Gender is required";

    const dobError = validateDOB(formData.dob);
    if (dobError) newErrors.dob = dobError;

    if (!formData.company.trim()) newErrors.company = "Company is required";

    if (!formData.password) newErrors.password = "Password is required";
    else if (formData.password.length < 6)
      newErrors.password = "Password must be at least 6 characters";

    if (!formData.password_confirmation)
      newErrors.password_confirmation = "Please confirm your password";
    else if (formData.password !== formData.password_confirmation)
      newErrors.password_confirmation = "Passwords do not match";

    return newErrors;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      await registerMutation.mutateAsync(formData);
    } catch (err) {
      console.error("Submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = useMemo(() => {
    return Object.values(formData).every((v) => v !== "") && !errors.dob;
  }, [formData, errors.dob]);

  const today = new Date().toISOString().split("T")[0];

  return (
    <section className="flex items-center justify-center bg-gray-50 p-4 min-h-screen">
      <div className="w-full max-w-md bg-white rounded-2xl p-6 lg:p-8 border shadow-sm">
        <div className="space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-gray-900">
              Accept Your Invitation
            </h2>
            <p className="text-gray-600 text-sm">
              Register to start your reconnection journey
            </p>
          </div>

          {successMessage && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-center font-medium">
              {successMessage}
            </div>
          )}

          {/* Use the extracted form fields */}
          <AcceptInviteFormFields
            formData={formData}
            errors={errors}
            showPassword={showPassword}
            showConfirmPassword={showConfirmPassword}
            isSubmitting={isSubmitting || registerMutation.isPending}
            isFormValid={isFormValid}
            today={today}
            handleChange={handleChange}
            togglePassword={() => setShowPassword(!showPassword)}
            toggleConfirmPassword={() =>
              setShowConfirmPassword(!showConfirmPassword)
            }
            handleSubmit={handleSubmit}
          />
        </div>
      </div>
    </section>
  );
};

export default AcceptInviteForm;
